import type { PopDevToolsChannel } from './types';

export interface PopDevToolsChannelInfo {
  id: number;
  name: PopDevToolsChannel;
  label: string;
  colorToken: string;
}

const channelNames = [
  'console',
  'redux',
  'ipc',
  'electron',
  'session',
  'floof',
  'rtc',
  'media',
  'poph264',
  'apps',
  'iago',
  'errors',
] as const satisfies readonly PopDevToolsChannel[];

export const POP_DEVTOOLS_CHANNELS: readonly PopDevToolsChannelInfo[] = channelNames.map((name, id) => ({
  id,
  name,
  label: name,
  colorToken: `--pop-devtools-channel-${name}`,
}));

const POP_DEVTOOLS_CHANNEL_IDS = POP_DEVTOOLS_CHANNELS.reduce(
  (ids, channel) => {
    ids[channel.name] = channel.id;
    return ids;
  },
  {} as Record<PopDevToolsChannel, number>,
);

export const TAG_TO_CHANNEL = {
  P2P: 'floof',
  'sfu-send': 'floof',
  'sfu-recv': 'floof',
  'webrtc-sdk': 'floof',
  'floof-connection': 'floof',
  'force-ice': 'floof',
  'relay-probe': 'floof',
  'rtc analytics': 'rtc',
  'pop-trace': 'media',
  'screen-share': 'media',
  'pop-recv': 'media',
  'desktop-screens': 'console',
  automation: 'console',
  'dev-auto-login': 'console',
} as const satisfies Record<string, PopDevToolsChannel>;

export const createEmptyChannelCounts = () =>
  POP_DEVTOOLS_CHANNELS.reduce(
    (counts, channel) => {
      counts[channel.name] = 0;
      return counts;
    },
    {} as Record<PopDevToolsChannel, number>,
  );

const normalizePopDevToolsTag = (tag: string) => tag.replace(/^\[/, '').replace(/\]$/, '');

export const resolvePopDevToolsChannelForTag = (tag: string): PopDevToolsChannel => {
  const normalized = normalizePopDevToolsTag(tag);
  const exact = TAG_TO_CHANNEL[normalized as keyof typeof TAG_TO_CHANNEL];
  if (exact) return exact;

  if (normalized.startsWith('pop-advice:')) return 'rtc';
  if (normalized.startsWith('PopH264')) return 'poph264';
  if (normalized.startsWith('IPC:')) return 'ipc';

  return 'console';
};

export const isKnownPopDevToolsTag = (tag: string) => {
  const normalized = normalizePopDevToolsTag(tag);
  return (
    normalized in TAG_TO_CHANNEL ||
    normalized.startsWith('pop-advice:') ||
    normalized.startsWith('PopH264') ||
    normalized.startsWith('IPC:')
  );
};
